import { watch, reactive, computed, toRaw } from "vue";
import { selDepartAndPersons, getPersonHrList } from "../apis/index";
import axios  from "axios";
import type { CancelTokenSource } from "axios";
import { ElMessage } from "element-plus";
import type { PersonSelector } from "../types/index";

type PersonList = PersonSelector.PersonList;

interface Data {
  keyword: string;
  loading: boolean;
  multiple: boolean;
  searchLoading: boolean;
  selectedCodes: string[];
  personList: PersonList[];
  selectedList: PersonList[];
  totalPersonList: PersonList[];
  searchPersonList: any[];
  cancelTokenSource: CancelTokenSource | null;
  pagination: { currentPage: number; totalPage: number };
}
interface Method {
  onConfirm: ((data: PersonList[]) => void) | null;
  onCancel: (() => void) | null;
}

const hookData = reactive<Data>({
  keyword: "",
  loading: true,
  multiple: false,
  searchLoading: false,
  cancelTokenSource: null,
  pagination: { currentPage: 1, totalPage: 1 },
  personList: [],
  searchPersonList: [],
  selectedCodes: [],
  selectedList: [],
  totalPersonList: [],
});

const hookMethod = reactive<Method>({
  onConfirm: null,
  onCancel: null,
});

const computedPersonList = computed<PersonList[]>(() => {
  const arr: PersonList[] = [];
  const res = hookData.totalPersonList.filter((item) =>
    hookData.selectedCodes.includes(item.personCode)
  );
  res.length &&
    hookData.selectedCodes.forEach((code) => {
      const item = res.find((it) => it.personCode === code);
      item && arr.push(item);
    });
  return arr;
});
const isDisabled = computed<boolean>(
  () =>
    hookData.pagination.currentPage > hookData.pagination.totalPage ||
    (hookData.pagination.currentPage === 1 && !hookData.keyword)
);

const onPersonListChange = (data: PersonList[]) => {
  hookData.personList = data;
};
const onLoadingChange = (status: boolean) => {
  hookData.loading = status;
};

const removeById = (id: string) => {
  hookData.selectedCodes = hookData.selectedCodes.filter((item) => item !== id);
};
const onKeywordChange = (keyword: string) => {
  hookData.pagination.currentPage = 1;
  hookData.cancelTokenSource && hookData.cancelTokenSource.cancel();
  keyword ? onLoadMore() : (hookData.searchPersonList = []);
};
const onLoadMore = () => {
  hookData.searchLoading = true;
  hookData.cancelTokenSource = axios.CancelToken.source();
  selDepartAndPersons(
    hookData.keyword,
    hookData.pagination.currentPage,
    hookData.cancelTokenSource.token
  )
    .then(({ responseData }:any) => {
      const pageData = responseData.personPage.pageData ?? [];
      const data = pageData.map((item:any) =>
        Object.assign({ personHierarchy: item.hierarchy }, item)
      );
      hookData.searchPersonList =
        hookData.pagination.currentPage === 1
          ? data
          : [...hookData.searchPersonList, ...data];
      hookData.pagination.totalPage = responseData.personPage.totalPages;
      hookData.pagination.totalPage >= hookData.pagination.currentPage &&
        hookData.pagination.currentPage++;
    })
    .catch((err:any) => {
      if (err.message === "canceled") return;
      ElMessage.error(err.message ?? err);
    })
    .finally(() => {
      hookData.searchLoading = false;
    });
};

const onConfirm = () => hookMethod.onConfirm!(toRaw(hookData.totalPersonList));
const onCancel = () => hookMethod.onCancel!();

const reset = () => {
  hookData.keyword = "";
  hookData.loading = true;
  hookData.personList = [];
  hookData.searchPersonList = [];
  hookData.selectedCodes = [];
  hookData.totalPersonList = [];
  hookData.pagination.currentPage = 1;
  hookData.pagination.totalPage = 1;
};

const getPersonByCodes = async (val: string[]) => {
  hookData.selectedCodes = val
  const { responseData } = await getPersonHrList(hookData.selectedCodes);
  hookData.totalPersonList = responseData;
};

// 单选下拉会预加载
watch(
  () => hookData.selectedCodes,
  (codes) => {
    if (hookData.multiple) {
      hookData.totalPersonList.push(
        ...hookData.personList
          .concat(hookData.searchPersonList)
          .filter(
            (item) =>
              codes.includes(item.personCode) &&
              !hookData.totalPersonList
                .map((p) => p.personCode)
                .includes(item.personCode)
          )
      );
    } else {
      hookData.totalPersonList.push(
        ...hookData.personList
          .concat(hookData.searchPersonList)
          .filter((item) => codes[0] === item.personCode)
      );
    }
  }
);
watch(
  () => hookData.keyword,
  (newVal) => onKeywordChange(newVal)
);

export {
  hookData,
  hookMethod,
  isDisabled,
  computedPersonList,
  onPersonListChange,
  onLoadingChange,
  getPersonByCodes,
  removeById,
  onLoadMore,
  onConfirm,
  onCancel,
  reset,
};
